import {
  TColorId,
  TColorReadoutValue,
  TDropdownId,
  TDropdownReadoutValue,
  TTypoId,
  TTypoReadoutValue,
} from "@ecp-redux/dto/themeSettings/themeSettings.types";
import { useLanguageCurrencyContext } from "@ecp-boxes/structure/Contexts/LanguageCurrencyContext";
import ThemeStyledDropdown from "@ecp-boxes/shared/styleElements/StyledDropdown/ThemeStyledDropdown";
import { useDispatch } from "react-redux";
import { api } from "@ecp-redux/api/api";
import * as currencyApi from "@ecp-redux/api/currency";
import {
  FieldValues,
  UseControllerProps,
  useFormContext,
  Path,
  useController,
} from "react-hook-form";
import StyledText from "@ecp-boxes/shared/styleElements/StyledText/StyledText";
import { useEffect, useMemo } from "react";
import { countries } from "countries-list";

interface ICountryDropdownProps<T extends FieldValues>
  extends Omit<UseControllerProps<T>, "name"> {
  withAllCountries?: boolean;
  name: Path<T>;
  dropdownStyleId: TDropdownReadoutValue | TDropdownId;
  color: TColorReadoutValue | TColorId;
  typography: TTypoReadoutValue | TTypoId;
  label?: string;
  listenForStorageLocaleChange?: boolean;
}

const CountryDropdown = <T extends FieldValues>({
  withAllCountries,
  dropdownStyleId,
  color,
  typography,
  label,
  listenForStorageLocaleChange = true,
  ...rest
}: ICountryDropdownProps<T>) => {
  const { languageCode, setStorageLocale, storageLocale, setCurrency } =
    useLanguageCurrencyContext();
  const { trigger, setValue, watch } = useFormContext<T>();
  const { field: countryField } = useController({ name: rest.name });
  const country = watch(rest.name);
  const dispatch = useDispatch();
  const { data: currencyData } = currencyApi.useGetCurrencyQuery();

  useEffect(() => {
    if (!countryField.value && listenForStorageLocaleChange) {
      countryField.onChange(storageLocale as any);
    }
  }, [storageLocale]);

  const handleSelectCountry = (itemId: string) => {
    if (!withAllCountries) {
      setStorageLocale(itemId);
      const selectedCurrency = currencyData?.configuredCurrencies?.find(
        (currency) => currency.locale === itemId
      );
      if (selectedCurrency) {
        setCurrency(selectedCurrency.currency);
        dispatch(
          api.util.invalidateTags(["CURRENCY_DROPDOWN", "LANGUAGE", "CART"])
        );
      }
    }
    setValue(rest.name, itemId as any);
    trigger(rest.name);
  };
  function getCountryName(locale: string, lang: string): string {
    let countryCode = "";
    if (locale.includes("_")) {
      countryCode = locale.split("_")[1];
    } else {
      countryCode = locale;
    }
    const countryNames = new Intl.DisplayNames([lang], {
      type: "region",
    });
    return countryNames.of(countryCode) || countryCode;
  }

  const allCountries = useMemo(() => {
    return Object.entries(countries).map(([code, country]) => ({
      id: `${country.languages[0]}_${code}`,
      label:
        new Intl.DisplayNames([languageCode], { type: "region" }).of(code) ||
        country.name,
    }));
  }, [languageCode]);

  const postalCountries = currencyData?.configuredCurrencies?.map(
    (currency) => ({
      id: currency.locale,
      label: `${getCountryName(currency.locale, languageCode)}`,
    })
  );

  return (
    <div>
      <StyledText
        className="country-dropdown__label"
        $settings={{
          text: {
            color,
            font: typography,
          },
        }}
      >
        {label}
      </StyledText>
      <ThemeStyledDropdown
        fullWidth
        key={country}
        items={(withAllCountries ? allCountries : postalCountries) ?? []}
        selectedItemId={withAllCountries ? country : storageLocale}
        onClickItem={handleSelectCountry}
        className="country-dropdown"
        style={dropdownStyleId}
      />
    </div>
  );
};
export default CountryDropdown;
